import { useState, useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts'
import { useAuth } from '../context/AuthContext'
import { useSensorData } from '../context/SensorDataContext'
import {
  assetApi,
  iotControllerApi,
  locationApi,
  organizationApi,
  departmentApi
} from '../api/services'

const ONLINE_STATUSES = new Set(['online', 'live', 'interval', 'warning'])

const resolveStatus = (ctrl, livePayloads) => {
  const lp = livePayloads[ctrl.id]
  const raw = (lp?.status || ctrl.status || 'offline').toLowerCase()
  return ONLINE_STATUSES.has(raw) ? 'online' : 'offline'
}

const CAN_WRITE_ROLES = ['super_admin', 'admin']

// Recursively flatten location tree → [{id, name, org_id, parent_id}]
const flattenTree = nodes => {
  const result = []
  const walk = list => {
    list.forEach(n => {
      result.push({
        id: n.id,
        name: n.name,
        parent_id: n.parent_id ?? null,
        org_id: n.org_id ?? n.organization_id ?? null
      })
      if (n.children?.length) walk(n.children)
    })
  }
  walk(Array.isArray(nodes) ? nodes : [])
  return result
}

// Org names for the "you're viewing" label (top-level orgs only)
const topLevelOrgs = nodes =>
  (Array.isArray(nodes) ? nodes : []).map(n => ({
    id: n.id,
    name: n.company_name || n.name
  }))

// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, sub, icon, color, to }) => (
  <Link to={to} className='group block'>
    <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-5 hover:shadow-md transition-all duration-200 flex items-center gap-4'>
      <div className={`p-3 rounded-xl ${color.bg} flex-shrink-0`}>{icon}</div>
      <div className='min-w-0'>
        <p className='text-xs font-semibold text-gray-400 uppercase tracking-widest truncate'>
          {title}
        </p>
        <p className={`text-3xl font-black tracking-tight ${color.text}`}>
          {value}
        </p>
        {sub && <p className='text-[11px] text-gray-400 mt-0.5'>{sub}</p>}
      </div>
    </div>
  </Link>
)

// ── Cascading org + location filter (super_admin only) ────────────────────────
// Filter inside the organization only: Site › Department › Asset.
// The org itself is implicit (the user is already inside it), so it is not shown.
const FilterBar = ({ sites, departments, assets, onChange }) => {
  const [siteId, setSiteId] = useState('')
  const [deptId, setDeptId] = useState('')
  const [assetId, setAssetId] = useState('')

  const deptOpts = siteId
    ? departments.filter(d => String(d.location_id) === String(siteId))
    : []
  const assetOpts = deptId
    ? assets.filter(a => String(a.department_id) === String(deptId))
    : siteId
    ? assets.filter(a => String(a.location_id) === String(siteId))
    : assets

  const notify = (s, d, a) =>
    onChange({ locationId: s || '', departmentId: d || '', assetId: a || '' })

  const handleSite = v => { setSiteId(v); setDeptId(''); setAssetId(''); notify(v, '', '') }
  const handleDept = v => { setDeptId(v); setAssetId(''); notify(siteId, v, '') }
  const handleAsset = v => { setAssetId(v); notify(siteId, deptId, v) }

  const hasFilter = siteId || deptId || assetId
  const sel =
    'text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 min-w-[150px]'

  const handleClear = () => {
    setSiteId(''); setDeptId(''); setAssetId('')
    onChange({ locationId: '', departmentId: '', assetId: '' })
  }

  const crumb = [
    sites.find(s => String(s.id) === String(siteId))?.name,
    departments.find(d => String(d.id) === String(deptId))?.name,
    assets.find(a => String(a.id) === String(assetId))?.name
  ].filter(Boolean).join(' › ')

  return (
    <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6'>
      <div className='flex flex-wrap items-center gap-3'>
        <span className='text-xs font-semibold text-gray-400 uppercase tracking-wide'>
          Filter
        </span>

        {/* Site */}
        <select className={sel} value={siteId} onChange={e => handleSite(e.target.value)}>
          <option value=''>All Sites</option>
          {sites.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        {/* Department (after a site is chosen) */}
        {siteId && (
          <select className={sel} value={deptId} onChange={e => handleDept(e.target.value)}>
            <option value=''>All Departments</option>
            {deptOpts.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        )}

        {/* Asset */}
        <select className={sel} value={assetId} onChange={e => handleAsset(e.target.value)}>
          <option value=''>All Assets</option>
          {assetOpts.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        {hasFilter && (
          <button
            onClick={handleClear}
            className='ml-auto text-xs text-gray-400 hover:text-red-500 transition-colors flex items-center gap-1'
          >
            <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
            Clear
          </button>
        )}
      </div>

      {crumb && <p className='mt-2 text-[11px] text-gray-400'>{crumb}</p>}
    </div>
  )
}

// ── Ring gauge (SVG) ─────────────────────────────────────────────────────────
const RingGauge = ({ pct, label, color, size = 88 }) => {
  const r = (size - 16) / 2
  const circ = 2 * Math.PI * r
  const dash = (Math.min(100, Math.max(0, pct)) / 100) * circ
  return (
    <div className='flex flex-col items-center gap-1'>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill='none' stroke='#f1f5f9' strokeWidth={9} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill='none'
          stroke={color} strokeWidth={9}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap='round'
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text x={size / 2} y={size / 2 + 1} textAnchor='middle' dominantBaseline='middle'
          fontSize={14} fontWeight='700' fill='#1e293b'>{pct}%</text>
      </svg>
      <p className='text-[10px] text-gray-500 font-medium text-center leading-tight'>{label}</p>
    </div>
  )
}

// ── Status pie ────────────────────────────────────────────────────────────────
const PIE_COLORS = { online: '#22c55e', offline: '#e5e7eb', warning: '#f59e0b' }

// ── Analytics panel ───────────────────────────────────────────────────────────
const AnalyticsPanel = ({ controllers, livePayloads, paramMappingsCache, trendData }) => {
  const total = controllers.length
  if (total === 0) return null

  const onlineList  = controllers.filter(c => resolveStatus(c, livePayloads) === 'online')
  const offlineList = controllers.filter(c => resolveStatus(c, livePayloads) === 'offline')
  const liveList    = controllers.filter(c => !!livePayloads[c.id]?.payload)
  const withAssets  = controllers.filter(c => c.asset_ids?.length > 0)

  const onlinePct = Math.round((onlineList.length / total) * 100)
  const livePct   = Math.round((liveList.length   / total) * 100)
  const assetPct  = Math.round((withAssets.length / total) * 100)

  const pieData = [
    { name: 'online',  value: onlineList.length  },
    { name: 'offline', value: offlineList.length },
  ].filter(d => d.value > 0)

  const scored = controllers.map(ctrl => {
    const isOn    = resolveStatus(ctrl, livePayloads) === 'online'
    const hasData = !!livePayloads[ctrl.id]?.payload
    const mappings = paramMappingsCache[ctrl.id] || {}
    const entries  = livePayloads[ctrl.id]?.payload?.Data
      ? Object.entries(livePayloads[ctrl.id].payload.Data) : []
    let out = 0
    entries.forEach(([key, val]) => {
      const m = mappings[key] || mappings[key.toLowerCase()]
      if (!m) return
      const n = parseFloat(val)
      if (!isNaN(n) && ((m.min_value != null && n < m.min_value) || (m.max_value != null && n > m.max_value))) out++
    })
    const score = !isOn ? 0 : !hasData ? 30 : Math.max(0, 100 - out * 20)
    return { ctrl, score, isOn, outCount: out }
  }).sort((a, b) => b.score - a.score)

  const top    = scored.filter(s => s.isOn && s.score > 0).slice(0, 3)
  const bottom = scored.filter(s => !s.isOn || s.outCount > 0).slice(0, 3)

  return (
    <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6'>
      <div className='flex items-center gap-2 mb-4'>
        <h2 className='text-sm font-bold text-gray-700'>Platform Analytics</h2>
        <span className='text-[10px] text-gray-400 bg-gray-50 border border-gray-100 rounded px-1.5 py-0.5'>Live</span>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>

        {/* Left – pie + ring gauges */}
        <div className='flex flex-col gap-4'>
          <div className='flex items-center gap-4'>
            <PieChart width={110} height={110} className='flex-shrink-0'>
              <Pie data={pieData} cx={50} cy={50} innerRadius={28} outerRadius={48}
                dataKey='value' paddingAngle={3}>
                {pieData.map(entry => (
                  <Cell key={entry.name} fill={PIE_COLORS[entry.name] || '#94a3b8'} />
                ))}
              </Pie>
            </PieChart>
            <div className='flex flex-col gap-1.5'>
              <p className='text-xs font-semibold text-gray-600 mb-0.5'>Status Split</p>
              {[
                { label: 'Online',  count: onlineList.length,  dot: 'bg-green-400' },
                { label: 'Offline', count: offlineList.length, dot: 'bg-gray-300'  },
              ].map(r => (
                <div key={r.label} className='flex items-center gap-2'>
                  <span className={`w-2 h-2 rounded-full ${r.dot}`} />
                  <span className='text-[11px] text-gray-500'>{r.label}</span>
                  <span className='text-[11px] font-bold text-gray-700 ml-auto'>{r.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className='flex justify-around pt-1 border-t border-gray-50'>
            <RingGauge pct={onlinePct} label='Online Rate'   color='#22c55e' />
            <RingGauge pct={livePct}   label='Live Streams'  color='#3b82f6' />
            <RingGauge pct={assetPct}  label='Asset Linked'  color='#8b5cf6' />
          </div>
        </div>

        {/* Middle – ranked controller lists */}
        <div className='flex flex-col gap-3'>
          <div>
            <p className='text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2'>Active Controllers</p>
            {top.length === 0
              ? <p className='text-xs text-gray-300'>No online controllers</p>
              : top.map(({ ctrl, score }) => (
                <div key={ctrl.id} className='flex items-center gap-2 mb-2'>
                  <svg className='w-3.5 h-3.5 text-green-400 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                  </svg>
                  <span className='text-[11px] text-gray-600 truncate w-20 flex-shrink-0'>{ctrl.name}</span>
                  <div className='flex-1 bg-gray-100 rounded-full h-1.5'>
                    <div className='bg-green-400 h-1.5 rounded-full transition-all duration-500' style={{ width: `${score}%` }} />
                  </div>
                  <span className='text-[10px] text-green-600 font-bold w-8 text-right'>{score}%</span>
                </div>
              ))
            }
          </div>
          <div className='border-t border-gray-50 pt-3'>
            <p className='text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2'>Needs Attention</p>
            {bottom.length === 0
              ? <p className='text-[11px] text-gray-300'>All controllers healthy</p>
              : bottom.map(({ ctrl, score }) => (
                <div key={ctrl.id} className='flex items-center gap-2 mb-2'>
                  <svg className='w-3.5 h-3.5 text-red-400 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z' />
                  </svg>
                  <span className='text-[11px] text-gray-600 truncate w-20 flex-shrink-0'>{ctrl.name}</span>
                  <div className='flex-1 bg-gray-100 rounded-full h-1.5'>
                    <div className='bg-red-400 h-1.5 rounded-full transition-all duration-500' style={{ width: `${Math.max(5, score)}%` }} />
                  </div>
                  <span className='text-[10px] text-red-500 font-bold w-8 text-right'>{score}%</span>
                </div>
              ))
            }
          </div>
        </div>

        {/* Right – online count trend line */}
        <div className='flex flex-col'>
          <p className='text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2'>Online Trend</p>
          {trendData.length < 2 ? (
            <div className='flex-1 flex items-center justify-center text-gray-300 text-xs py-10'>
              Collecting data…
            </div>
          ) : (
            <ResponsiveContainer width='100%' height={175}>
              <LineChart data={trendData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <CartesianGrid strokeDasharray='3 3' stroke='#f3f4f6' vertical={false} />
                <XAxis dataKey='t' tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false}
                  axisLine={false} interval='preserveStartEnd' />
                <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false}
                  allowDecimals={false} />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb', padding: '4px 10px' }}
                  formatter={(v, n) => [v, n === 'online' ? 'Online' : 'Total']}
                />
                <Line type='monotone' dataKey='total'  stroke='#e2e8f0' strokeWidth={1.5}
                  dot={false} strokeDasharray='4 2' isAnimationActive={false} />
                <Line type='monotone' dataKey='online' stroke='#8b5cf6' strokeWidth={2}
                  dot={false} activeDot={{ r: 3, strokeWidth: 0 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
          <div className='flex gap-4 mt-1'>
            <div className='flex items-center gap-1.5'>
              <span className='w-4 h-0.5 bg-violet-500 rounded inline-block' />
              <span className='text-[10px] text-gray-400'>Online</span>
            </div>
            <div className='flex items-center gap-1.5'>
              <span className='w-4 h-0.5 bg-gray-200 rounded inline-block' />
              <span className='text-[10px] text-gray-400'>Total</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

// ── Sensor readings grid ──────────────────────────────────────────────────────
const SensorReadings = ({ dataFields, paramMappings }) => (
  <div className='grid grid-cols-3 gap-1.5'>
    {dataFields.slice(0, 9).map(([key, val]) => {
      const m = paramMappings?.[key.toLowerCase()] || paramMappings?.[key]
      const label = m?.display_name || key.toUpperCase()
      const unit = m?.unit || ''
      const num = parseFloat(val)
      const min = m?.min_value ?? null
      const max = m?.max_value ?? null
      const outOfRange =
        !isNaN(num) &&
        ((min !== null && num < min) || (max !== null && num > max))
      return (
        <div
          key={key}
          className={`rounded-lg px-2 py-1.5 text-center border ${
            outOfRange
              ? 'bg-red-50 border-red-200'
              : 'bg-blue-50 border-blue-100'
          }`}
        >
          <p
            className={`text-[9px] uppercase tracking-wide truncate ${
              outOfRange ? 'text-red-400' : 'text-blue-400'
            }`}
            title={label}
          >
            {label}
          </p>
          <p
            className={`font-mono font-bold text-xs mt-0.5 ${
              outOfRange ? 'text-red-600' : 'text-blue-700'
            }`}
          >
            {val ?? '—'}
            {unit && (
              <span className='text-[9px] font-normal ml-0.5 opacity-70'>
                {unit}
              </span>
            )}
          </p>
          {outOfRange && (
            <p className='text-[8px] text-red-400 leading-none'>
              {num < min ? `↓min ${min}` : `↑max ${max}`}
            </p>
          )}
        </div>
      )
    })}
  </div>
)

// ── Controller widget ─────────────────────────────────────────────────────────
const ControllerWidget = ({ ctrl, livePayload, paramMappings }) => {
  const isOnline = resolveStatus(ctrl, { [ctrl.id]: livePayload }) === 'online'
  const dataFields = livePayload?.payload?.Data
    ? Object.entries(livePayload.payload.Data)
    : []
  const ts = livePayload?.receivedAt
    ? new Date(livePayload.receivedAt).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    : null

  return (
    <div
      className={`bg-white rounded-xl shadow-sm flex flex-col hover:shadow-md transition-shadow border ${
        isOnline ? 'border-gray-200' : 'border-gray-100'
      }`}
    >
      {/* Header */}
      <div className='p-4 flex items-start justify-between'>
        <div className='min-w-0 flex-1'>
          <p className='font-bold text-gray-800 text-sm truncate'>
            {ctrl.name}
          </p>
          <div className='flex flex-wrap gap-1 mt-1.5'>
            {ctrl.location_name && (
              <span className='inline-flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5'>
                <svg
                  className='w-2.5 h-2.5'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                  />
                </svg>
                {ctrl.location_name}
              </span>
            )}
            {ctrl.asset_names?.map(name => (
              <span
                key={name}
                className='text-[10px] bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-2 py-0.5'
              >
                {name}
              </span>
            ))}
          </div>
        </div>
        {/* Status */}
        <div className='flex items-center gap-1.5 flex-shrink-0 ml-3'>
          {isOnline ? (
            <span className='relative flex h-2.5 w-2.5'>
              <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75' />
              <span className='relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500' />
            </span>
          ) : (
            <span className='w-2.5 h-2.5 rounded-full bg-gray-300' />
          )}
          <span
            className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${
              isOnline
                ? 'text-green-700 bg-green-50 border-green-200'
                : 'text-gray-500 bg-gray-50 border-gray-200'
            }`}
          >
            {isOnline ? 'Online' : 'Offline'}
          </span>
        </div>
      </div>

      {/* Sensor readings */}
      <div className={`px-4 pb-3 flex-1 ${!isOnline ? 'opacity-60' : ''}`}>
        {dataFields.length > 0 ? (
          <SensorReadings
            dataFields={dataFields}
            paramMappings={paramMappings}
          />
        ) : (
          <div className='flex flex-col items-center justify-center py-5 text-gray-300'>
            <svg
              className='w-7 h-7 mb-1.5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.143 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0'
              />
            </svg>
            <p className='text-xs'>
              {isOnline ? 'No sensor data in payload' : 'Waiting for data…'}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className='px-4 py-2 border-t border-gray-50 flex items-center justify-between'>
        <span
          className='text-[10px] text-gray-300 font-mono truncate max-w-[70%]'
          title={ctrl.topic}
        >
          {ctrl.topic || '—'}
        </span>
        <div className='flex items-center gap-2 flex-shrink-0'>
          {ts && <span className='text-[10px] text-gray-400'>{ts}</span>}
          {isOnline && (
            <Link
              to='/data-monitoring'
              className='text-[10px] text-blue-600 hover:text-blue-800 font-medium'
            >
              Charts →
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main dashboard ────────────────────────────────────────────────────────────
const DashboardHome = () => {
  const { user, activeTenant } = useAuth()
  const { livePayloads, paramMappingsCache, loadParamMappings } =
    useSensorData()

  const resolvedTenantId =
    (typeof activeTenant === 'object' ? activeTenant?.id : activeTenant) ??
    user?.tenant_id

  const isSuperAdmin = user?.role === 'super_admin'
  const canWrite = CAN_WRITE_ROLES.includes(user?.role)

  const [orgs, setOrgs] = useState([])
  const [locations, setLocations] = useState([])
  const [departments, setDepartments] = useState([])
  const [assets, setAssets] = useState([])
  const [controllers, setControllers] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  // Filter inside the organization only (Site › Department › Asset)
  const [filters, setFilters] = useState({
    locationId: '',
    departmentId: '',
    assetId: ''
  })
  const [statusTab, setStatusTab] = useState('all')

  // Trend tracking: accumulate online count per livePayloads tick
  const trendRef = useRef([])
  const [trendData, setTrendData] = useState([])
  useEffect(() => {
    if (controllers.length === 0) return
    const online = controllers.filter(c => resolveStatus(c, livePayloads) === 'online').length
    const now = new Date()
    const t = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`
    trendRef.current = [...trendRef.current, { t, online, total: controllers.length }].slice(-20)
    setTrendData([...trendRef.current])
  }, [livePayloads, controllers])

  useEffect(() => {
    if (!resolvedTenantId) return
    setIsLoading(true)
    Promise.all([
      organizationApi.getAll({ tenantId: resolvedTenantId }),
      locationApi.getAll({ tenantId: resolvedTenantId }),
      assetApi.getAll({ tenantId: resolvedTenantId }),
      iotControllerApi.getAll(resolvedTenantId),
      departmentApi.getAll({ tenantId: resolvedTenantId }).catch(() => ({ data: [] }))
    ])
      .then(([orgRes, locRes, assetRes, ctrlRes, deptRes]) => {
        const rawOrgs = orgRes.data?.data || orgRes.data || []
        const orgList = Array.isArray(rawOrgs) ? rawOrgs : []
        setOrgs(topLevelOrgs(orgList))
        const rawLocs = locRes.data?.data || locRes.data || []
        setLocations(flattenTree(Array.isArray(rawLocs) ? rawLocs : []))
        const deptData = deptRes.data?.data || deptRes.data || []
        setDepartments(Array.isArray(deptData) ? deptData : [])
        const assetData = assetRes.data?.data || assetRes.data || []
        setAssets(Array.isArray(assetData) ? assetData : assetData.items || [])
        const ctrlData = ctrlRes.data?.data || ctrlRes.data || []
        const ctrls = Array.isArray(ctrlData) ? ctrlData : []
        setControllers(ctrls)
        loadParamMappings(ctrls)
      })
      .catch(err => console.error('[Dashboard] load error', err))
      .finally(() => setIsLoading(false))
  }, [resolvedTenantId, loadParamMappings])

  // Fast lookup: asset_id → asset
  const assetMap = useMemo(() => {
    const m = {}
    assets.forEach(a => { m[String(a.id)] = a })
    return m
  }, [assets])

  // Filter controllers by site/department/asset + status tab (all inside the org)
  const filteredControllers = useMemo(() => {
    const ctrlAssets = ctrl => (ctrl.asset_ids || []).map(id => assetMap[String(id)]).filter(Boolean)
    return controllers.filter(ctrl => {
      // Site (location) filter — match the controller's own location OR any of its assets' site
      if (filters.locationId) {
        const ok = String(ctrl.location_id) === String(filters.locationId) ||
          ctrlAssets(ctrl).some(a => String(a.location_id) === String(filters.locationId))
        if (!ok) return false
      }

      // Department filter — via the controller's assets
      if (filters.departmentId) {
        if (!ctrlAssets(ctrl).some(a => String(a.department_id) === String(filters.departmentId)))
          return false
      }

      // Asset filter
      if (filters.assetId) {
        const id = Number(filters.assetId)
        if (
          !ctrl.asset_ids?.includes(id) &&
          !ctrl.asset_ids?.includes(String(id))
        )
          return false
      }

      // Status tab
      if (
        statusTab !== 'all' &&
        resolveStatus(ctrl, livePayloads) !== statusTab
      )
        return false

      return true
    })
  }, [
    controllers,
    filters,
    assetMap,
    statusTab,
    livePayloads
  ])

  // Stat counts (always from all controllers, not filtered)
  const stats = useMemo(() => {
    const online = controllers.filter(
      c => resolveStatus(c, livePayloads) === 'online'
    ).length
    const live = controllers.filter(c => !!livePayloads[c.id]?.payload).length
    return { total: controllers.length, online, live }
  }, [controllers, livePayloads])

  const tabCounts = useMemo(
    () => ({
      all: filteredControllers.length,
      online: filteredControllers.filter(
        c => resolveStatus(c, livePayloads) === 'online'
      ).length,
      offline: filteredControllers.filter(
        c => resolveStatus(c, livePayloads) === 'offline'
      ).length
    }),
    [filteredControllers, livePayloads]
  )

  const hour = new Date().getHours()
  const greeting =
    hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const displayName =
    user?.full_name || activeTenant?.name || user?.org_name || 'there'

  // Org label shown to non-super_admin so they know which org they're viewing
  const myOrgName =
    !isSuperAdmin && user?.org_id
      ? orgs.find(o => String(o.id) === String(user.org_id))?.name ||
        user?.org_name
      : null

  return (
    <div className='p-6 lg:p-8 min-h-screen bg-gray-50 font-sans'>
      {/* Header */}
      <div className='mb-6 flex items-start justify-between'>
        <div>
          <p className='text-sm text-gray-400'>{greeting},</p>
          <h1 className='text-2xl font-black text-gray-800 tracking-tight'>
            {displayName}
          </h1>
          <p className='text-sm text-gray-500 mt-0.5'>
            Live overview of your industrial IoT platform
          </p>
        </div>
        {/* Show org badge for non-super_admin */}
        {myOrgName && (
          <span className='text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1.5'>
            <svg
              className='w-3.5 h-3.5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
              />
            </svg>
            {myOrgName}
          </span>
        )}
      </div>

      {/* Stat cards */}
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
        <StatCard
          title='Controllers'
          value={isLoading ? '—' : stats.total}
          sub={`${stats.online} online`}
          to='/iot-controllers'
          color={{ bg: 'bg-indigo-50', text: 'text-indigo-800' }}
          icon={
            <svg
              className='w-5 h-5 text-indigo-700'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.143 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0'
              />
            </svg>
          }
        />
        <StatCard
          title='Online'
          value={isLoading ? '—' : stats.online}
          sub='receiving data'
          to='/iot-controllers'
          color={{ bg: 'bg-green-50', text: 'text-green-700' }}
          icon={
            <svg
              className='w-5 h-5 text-green-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          }
        />
        <StatCard
          title='Assets'
          value={isLoading ? '—' : assets.length}
          sub={`${locations.length} locations`}
          to='/assets'
          color={{ bg: 'bg-purple-50', text: 'text-purple-800' }}
          icon={
            <svg
              className='w-5 h-5 text-purple-700'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 5.625c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125'
              />
            </svg>
          }
        />
        <StatCard
          title='Live Data'
          value={isLoading ? '—' : stats.live}
          sub='active streams'
          to='/data-monitoring'
          color={{ bg: 'bg-emerald-50', text: 'text-emerald-700' }}
          icon={
            <svg
              className='w-5 h-5 text-emerald-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z'
              />
            </svg>
          }
        />
      </div>

      {/* Analytics panel */}
      {!isLoading && (
        <AnalyticsPanel
          controllers={controllers}
          livePayloads={livePayloads}
          paramMappingsCache={paramMappingsCache}
          trendData={trendData}
        />
      )}

      {/* Filter inside the organization: Site › Department › Asset */}
      <FilterBar
        sites={locations.filter(l => !l.parent_id)}
        departments={departments}
        assets={assets}
        onChange={setFilters}
      />

      {/* Status tabs */}
      <div className='flex items-center justify-between mb-4 flex-wrap gap-2'>
        <div className='flex gap-1'>
          {[
            { id: 'all', label: `All (${tabCounts.all})` },
            { id: 'online', label: `Online (${tabCounts.online})` },
            { id: 'offline', label: `Offline (${tabCounts.offline})` }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setStatusTab(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusTab === tab.id
                  ? 'bg-blue-800 text-white'
                  : 'text-gray-600 hover:bg-gray-100 bg-white border border-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <Link
          to='/iot-controllers'
          className='text-xs text-blue-700 font-medium hover:underline'
        >
          Manage controllers →
        </Link>
      </div>

      {/* Controller widgets */}
      {isLoading ? (
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
          {[1, 2, 3, 4, 5, 6].map(n => (
            <div
              key={n}
              className='bg-white rounded-xl border border-gray-200 p-4 h-44 animate-pulse'
            >
              <div className='h-4 bg-gray-100 rounded w-1/2 mb-2' />
              <div className='h-3 bg-gray-50 rounded w-1/3 mb-6' />
              <div className='grid grid-cols-3 gap-2'>
                {[1, 2, 3].map(i => (
                  <div key={i} className='h-10 bg-gray-50 rounded-lg' />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : filteredControllers.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200'>
          <svg
            className='w-12 h-12 mb-3 opacity-30'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={1.5}
              d='M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.143 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0'
            />
          </svg>
          <p className='font-semibold'>
            No controllers match the current filters
          </p>
          <p className='text-sm mt-1'>Try adjusting the filters above</p>
          {canWrite && (
            <div className='flex gap-3 mt-5'>
              <Link
                to='/assets'
                className='px-4 py-2 bg-blue-900 text-white text-sm font-semibold rounded-lg hover:bg-blue-800'
              >
                Add Asset
              </Link>
              <Link
                to='/iot-controllers'
                className='px-4 py-2 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50'
              >
                Add Controller
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
          {filteredControllers.map(ctrl => (
            <ControllerWidget
              key={ctrl.id}
              ctrl={ctrl}
              livePayload={livePayloads[ctrl.id]}
              paramMappings={paramMappingsCache[ctrl.id]}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default DashboardHome
